// src/modules/stock/StockQueryPage.js

import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import useFirestore from '../../hooks/useFirestore';
import useSubCollection from '../../hooks/useSubCollection';
import { db } from '../../firebaseConfig';
import {
    collection,
    onSnapshot,
    getDocs,
    query,
    where,
    doc,
    limit,
    orderBy,
    getCountFromServer,
    updateDoc,
    writeBatch,
    runTransaction
} from 'firebase/firestore';
import StockMovementModal from './StockMovementModal';
import { exportInventory } from '../../documents';
import styles from './Logistics.module.css';
import { GoArrowSwitch, GoAlertFill } from 'react-icons/go'
import { FiPrinter } from 'react-icons/fi'
import { GiSandsOfTime } from "react-icons/gi";
import { IoReload } from "react-icons/io5";
import { PiKeyReturnLight } from "react-icons/pi";
import {
    autoCorrectAllStock,
    autoCorrectStockInconsistency
} from '../../hooks/stockService';
import usePagePermissions from '../../hooks/usePagePermissions';

import { PDFDownloadLink } from '@react-pdf/renderer';
import LabelDocument from './LabelDocument';

// Import global components
import {
    FormModal,
    ConfirmModal,
    SearchBar,
    Pagination,
    TableSkeleton,
    EmptyState,
    Toast,
    PageHeader
} from '../../components/common';

const ITEMS_PER_PAGE = 20;
const CURRENT_STOCK_COLLECTION = 'current-stock';
const RAW_MATERIALS_COLLECTION = 'raw-materials';
const PRODUCT_TYPE_COLLECTION_PATH = {
    parentCollection: 'parameters',
    parentId: 'types',
    subCollectionName: 'tipo-produto'
};
const CATEGORY_COLLECTION = 'parameters/data/product-categories';
const PRODUCTS_COLLECTION = 'products';
const STOCK_ENTRIES_COLLECTION = 'stock-entries';
const STOCK_OUTFLOWS_COLLECTION = 'stock-outflows';
const SUPPLIERS_COLLECTION = 'suppliers';

// Configuração da Água
const WATER_MATERIALS = ['MP001', 'AGUA001', 'WATER001'];

// Status de validade possíveis
const EXPIRY_STATUS = {
    EXPIRED: 'EXPIRED',
    PENDING_REVIEW: 'PENDING_REVIEW',
    APPROVED: 'APPROVED',
    REJECTED: 'REJECTED',
    REPROCESSED: 'REPROCESSED'
};

// Configuração de cores para cada status
const EXPIRY_STATUS_CONFIG = {
    [EXPIRY_STATUS.EXPIRED]: {
        label: 'Vencido',
        bgColor: '#e53e3e',
        color: 'white',
        icon: '🔴'
    },
    [EXPIRY_STATUS.PENDING_REVIEW]: {
        label: 'Aguardando Análise',
        bgColor: '#ed8936',
        color: 'white',
        icon: '⏳'
    },
    [EXPIRY_STATUS.APPROVED]: {
        label: 'Aprovado',
        bgColor: '#38a169',
        color: 'white',
        icon: '✅'
    },
    [EXPIRY_STATUS.REJECTED]: {
        label: 'Rejeitado',
        bgColor: '#805ad5',
        color: 'white',
        icon: '❌'
    },
    [EXPIRY_STATUS.REPROCESSED]: {
        label: 'Reprocessado',
        bgColor: '#3182ce',
        color: 'white',
        icon: '🔄'
    }
};

// Helper para verificar se é água
const isWaterMaterial = (material) => {
    if (!material) return false;
    const code = material.code || material.rawMaterialCode || '';
    return WATER_MATERIALS.includes(code.toUpperCase());
};

// Helper para verificar se está expirado
const isExpired = (expirationDate) => {
    if (!expirationDate) return false;
    try {
        let expiry;

        if (expirationDate.toDate) {
            expiry = expirationDate.toDate();
        }
        else if (typeof expirationDate === 'string') {
            const parts = expirationDate.split('-');
            if (parts.length === 3) {
                expiry = new Date(parseInt(parts[0]), parseInt(parts[1]) - 1, parseInt(parts[2]), 12, 0, 0);
            } else {
                expiry = new Date(expirationDate);
            }
        }
        else if (expirationDate instanceof Date) {
            expiry = expirationDate;
        }
        else {
            expiry = new Date(expirationDate);
        }

        if (isNaN(expiry.getTime())) return false;

        const today = new Date();
        today.setHours(0, 0, 0, 0);

        const expiryDate = new Date(expiry.getFullYear(), expiry.getMonth(), expiry.getDate());
        return expiryDate < today;
    } catch {
        return false;
    }
};

// Helper para formatar data
const formatDate = (dateValue) => {
    if (!dateValue) return '—';
    try {
        let date;

        if (dateValue.toDate) {
            date = dateValue.toDate();
            const day = String(date.getDate()).padStart(2, '0');
            const month = String(date.getMonth() + 1).padStart(2, '0');
            const year = date.getFullYear();
            return `${day}/${month}/${year}`;
        }

        if (typeof dateValue === 'string') {
            const parts = dateValue.split('-');
            if (parts.length === 3) {
                const year = parseInt(parts[0]);
                const month = parseInt(parts[1]);
                const day = parseInt(parts[2]);
                date = new Date(year, month - 1, day, 12, 0, 0);
                if (!isNaN(date.getTime())) {
                    const d = String(date.getDate()).padStart(2, '0');
                    const m = String(date.getMonth() + 1).padStart(2, '0');
                    const y = date.getFullYear();
                    return `${d}/${m}/${y}`;
                }
            }

            date = new Date(dateValue);
            if (!isNaN(date.getTime())) {
                const day = String(date.getDate()).padStart(2, '0');
                const month = String(date.getMonth() + 1).padStart(2, '0');
                const year = date.getFullYear();
                return `${day}/${month}/${year}`;
            }
            return 'Inválida';
        }

        if (dateValue instanceof Date) {
            const day = String(dateValue.getDate()).padStart(2, '0');
            const month = String(dateValue.getMonth() + 1).padStart(2, '0');
            const year = dateValue.getFullYear();
            return `${day}/${month}/${year}`;
        }

        date = new Date(dateValue);
        if (!isNaN(date.getTime())) {
            const day = String(date.getDate()).padStart(2, '0');
            const month = String(date.getMonth() + 1).padStart(2, '0');
            const year = date.getFullYear();
            return `${day}/${month}/${year}`;
        }

        return 'Inválida';
    } catch {
        return 'Inválida';
    }
};

// Cache service
class CacheService {
    constructor(ttl = 300000) {
        this.cache = new Map();
        this.ttl = ttl;
    }

    get(key) {
        const cached = this.cache.get(key);
        if (cached && Date.now() - cached.timestamp < this.ttl) {
            return cached.data;
        }
        return null;
    }

    set(key, data) {
        this.cache.set(key, { data, timestamp: Date.now() });
    }
}

const cache = new CacheService();

// Componente Modal de Histórico com Live Updates e Lote
const ItemHistoryModal = ({
    isOpen,
    onClose,
    itemDetails,
    history,
    loading,
    onRetry,
    t
}) => {
    if (!isOpen) return null;

    const formatTimestamp = (timestamp) => {
        if (!timestamp) return 'N/A';
        try {
            const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
            return date.toLocaleString('pt-BR');
        } catch {
            return 'Data inválida';
        }
    };

    const getTypeStyle = (type, isWater = false) => {
        if (isWater) {
            return { label: t('pages.stock.query.waterEntry', 'ENTRADA (Água)'), className: styles.waterRow };
        }
        switch (type) {
            case 'TRANSFER_OUT': return { label: t('pages.stock.query.transferOut', 'TRANSFERÊNCIA SAÍDA'), className: styles.outflowRow };
            case 'TRANSFER_IN': return { label: t('pages.stock.query.transferIn', 'TRANSFERÊNCIA ENTRADA'), className: styles.entryRow };
            case 'ENTRADA': return { label: t('pages.stock.query.entry', 'ENTRADA'), className: styles.entryRow };
            case 'SAÍDA': return { label: t('pages.stock.query.outflow', 'SAÍDA'), className: styles.outflowRow };
            default: return { label: type, className: '' };
        }
    };

    return (
        <div className={styles.modalBackdrop}>
            <div className={styles.historyModalContent}>
                <h2 className={styles.historyModalTitle}>
                    {t('pages.stock.query.historyTitle', 'Histórico de Movimentação:')}
                    <br />
                    <span style={{ color: 'var(--primary-color)' }}>
                        {itemDetails?.name} ({itemDetails?.code})
                    </span>
                    <br />
                    <span style={{ fontSize: '1rem', color: 'var(--secondary-color)' }}>
                        {itemDetails?.isWater ? t('pages.stock.query.infiniteWater', 'Recurso Infinito (Água)') : `${t('pages.stock.query.locationLabel', 'Localização:')} ${itemDetails?.location || 'N/A'}`}
                    </span>
                </h2>

                <div className={styles.historyTableContainer}>
                    <table className={styles.table}>
                        <thead>
                            <tr>
                                <th style={{ width: '20%' }}>{t('pages.stock.query.historyDate', 'Data')}</th>
                                <th style={{ width: '12%' }}>{t('pages.stock.query.historyType', 'Tipo')}</th>
                                <th style={{ width: '12%' }}>{t('pages.stock.query.historyLot', 'Lote')}</th>
                                <th style={{ width: '12%' }}>{t('pages.stock.query.historyExpiration', 'Validade')}</th>
                                <th style={{ width: '15%' }}>{t('pages.stock.query.historyUser', 'Usuário')}</th>
                                <th style={{ width: '12%', textAlign: 'right' }}>{t('pages.stock.query.historyQuantity', 'Quantidade')}</th>
                                <th>{t('pages.stock.query.historyNotes', 'Observação')}</th>
                            </tr>
                        </thead>
                        <tbody>
                            {loading && (
                                <tr><td colSpan={7} className={styles.loadingCell}>
                                    <div className={styles.spinner}></div>
                                    {t('pages.stock.query.loadingHistory', 'Carregando histórico...')}
                                </td></tr>
                            )}
                            {!loading && history.length === 0 && (
                                <tr><td colSpan={7} className={styles.emptyTable}>
                                    {t('pages.stock.query.noHistory', 'Nenhum histórico encontrado para este item.')}
                                    {itemDetails?.isWater && ` ${t('pages.stock.query.waterNoHistory', '(Água é um recurso infinito)')}`}
                                </td></tr>
                            )}
                            {!loading && history.map(mov => {
                                const { label, className } = getTypeStyle(mov.type, mov.isWater);
                                const isInbound = mov.type === 'ENTRADA' || mov.type === 'TRANSFER_IN';
                                return (
                                    <tr key={mov.id} className={className}>
                                        <td>
                                            {formatTimestamp(mov.createdAt)}
                                            {mov.updatedAt && (
                                                <span style={{ display: 'block', fontSize: '0.7rem', color: 'gray' }}>
                                                    ({t('pages.stock.query.edited', 'Editado')})
                                                </span>
                                            )}
                                        </td>
                                        <td>{label}</td>
                                        <td>{mov.lotNumber || '—'}</td>
                                        <td>{mov.expirationDate ? formatDate(mov.expirationDate) : '—'}</td>
                                        <td>{mov.updatedBy || mov.createdBy || 'N/A'}</td>
                                        <td style={{ textAlign: 'right', fontWeight: 'bold' }}>
                                            {isInbound ? '+' : '-'}
                                            {Number(mov.displayQuantity || 0).toFixed(3)}
                                            {mov.isWater && <span style={{ fontSize: '10px', marginLeft: '4px' }}>L</span>}
                                        </td>
                                        <td>{mov.notes || '---'}</td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>

                <div className={styles.modalActions} style={{ marginTop: '20px', borderTop: 'none' }}>
                    <button type="button" onClick={onClose} className={styles.cancelButton}>
                        {t('common.close', 'Fechar')}
                    </button>
                    {history.length === 0 && !loading && onRetry && (
                        <button type="button" onClick={onRetry} className={styles.addButton}>
                            {t('pages.stock.query.retry', 'Tentar Novamente')}
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
};

// Componente Modal de Estoque Baixo
const LowStockModal = ({ isOpen, onClose, items, loading, t }) => {
    if (!isOpen) return null;

    return (
        <div className={styles.modalBackdrop}>
            <div className={styles.historyModalContent}>
                <h2 className={styles.historyModalTitle}>
                    {t('pages.stock.query.lowStockTitle', 'Itens com Estoque Baixo')}
                    <br />
                    <span style={{ fontSize: '1rem', color: 'var(--secondary-color)' }}>
                        {t('pages.stock.query.lowStockSubtitle', 'Saldo total abaixo de 500 unidades')}
                    </span>
                </h2>

                <div className={styles.historyTableContainer}>
                    <table className={styles.table}>
                        <thead>
                            <tr>
                                <th style={{ width: '100px' }}>{t('pages.stock.query.lowStockCode', 'Código')}</th>
                                <th>{t('pages.stock.query.lowStockItem', 'Item')}</th>
                                <th style={{ width: '120px' }}>{t('pages.stock.query.lowStockLot', 'Lote')}</th>
                                <th style={{ width: '100px' }}>{t('pages.stock.query.lowStockExpiration', 'Validade')}</th>
                                <th style={{ width: '150px', textAlign: 'right' }}>{t('pages.stock.query.lowStockBalance', 'Saldo Total')}</th>
                            </tr>
                        </thead>
                        <tbody>
                            {loading ? (
                                <tr><td colSpan={5} className={styles.loadingCell}>{t('common.loading', 'Carregando...')}</td></tr>
                            ) : items.length === 0 ? (
                                <tr>
                                    <td colSpan={5} className={styles.emptyTable}>
                                        {t('pages.stock.query.noLowStock', 'Nenhum item com saldo abaixo de 500.')}
                                    </td>
                                </tr>
                            ) : (
                                items.slice(0, 50).map(item => (
                                    <tr key={`${item.rawMaterialId}_${item.lotNumber || ''}`} className={styles.outflowRow}>
                                        <td>{item.rawMaterialCode}</td>
                                        <td>{item.rawMaterialName}</td>
                                        <td>{item.lotNumber || '—'}</td>
                                        <td>{item.expirationDate ? formatDate(item.expirationDate) : '—'}</td>
                                        <td style={{ textAlign: 'right', fontWeight: 'bold', color: 'var(--danger-color, #e53e3e)' }}>
                                            {item.totalQuantity.toLocaleString('pt-BR', {
                                                minimumFractionDigits: 3,
                                                maximumFractionDigits: 3
                                            })}
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>

                <div className={styles.modalActions} style={{ marginTop: '20px', borderTop: 'none' }}>
                    <button type="button" onClick={onClose} className={styles.cancelButton}>
                        {t('common.close', 'Fechar')}
                    </button>
                </div>
            </div>
        </div>
    );
};

// --- Componente Principal ---
const StockQueryPage = () => {
    const { t } = useTranslation();
    const can = usePagePermissions('consulta_de_estoque');
    const [currentStock, setCurrentStock] = useState([]);
    const [waterMaterials, setWaterMaterials] = useState([]);
    const [loading, setLoading] = useState(false);
    const [loadingWater, setLoadingWater] = useState(false);
    const [status, setStatus] = useState({ message: '', type: '' });
    const [page, setPage] = useState(1);
    const [searchTerm, setSearchTerm] = useState('');
    const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
    const [filterProductTypeId, setFilterProductTypeId] = useState('');
    const [filterCategoryId, setFilterCategoryId] = useState('');

    // Supplier data cache
    const [suppliersMap, setSuppliersMap] = useState(new Map());
    const [loadingSuppliers, setLoadingSuppliers] = useState(false);

    // Estados do Modal de Histórico
    const [isHistoryModalOpen, setIsHistoryModalOpen] = useState(false);
    const [historyLoading, setHistoryLoading] = useState(false);
    const [selectedItemHistory, setSelectedItemHistory] = useState([]);
    const [selectedItemDetails, setSelectedItemDetails] = useState(null);
    const [historyUnsubscribe, setHistoryUnsubscribe] = useState(null);

    // Estados do Modal de Estoque Baixo e Transferência
    const [isLowStockModalOpen, setIsLowStockModalOpen] = useState(false);
    const [isMovementModalOpen, setIsMovementModalOpen] = useState(false);
    const [selectedItemForTransfer, setSelectedItemForTransfer] = useState(null);

    // Estados para controle de status de validade
    const [expiryStatusMap, setExpiryStatusMap] = useState(new Map());
    const [updatingStatus, setUpdatingStatus] = useState(false);

    // Referência para controlar auto-correção
    const correctionTimeoutRef = useRef(null);
    const lastCorrectionRef = useRef({});

    // Hooks
    const {
        data: allRawMaterials,
        loading: loadingRawMaterials
    } = useFirestore(RAW_MATERIALS_COLLECTION, 'code');

    const { data: products, loading: loadingProducts } = useFirestore(PRODUCTS_COLLECTION, 'code');

    const { data: productTypes, loading: loadingTypes } = useSubCollection(
        PRODUCT_TYPE_COLLECTION_PATH.parentCollection,
        PRODUCT_TYPE_COLLECTION_PATH.parentId,
        PRODUCT_TYPE_COLLECTION_PATH.subCollectionName,
        'name'
    );

    const { data: categories, loading: loadingCategories } = useFirestore(CATEGORY_COLLECTION, 'order');

    // Load suppliers data
    useEffect(() => {
        const loadSuppliers = async () => {
            setLoadingSuppliers(true);
            try {
                const suppliersSnapshot = await getDocs(collection(db, SUPPLIERS_COLLECTION));
                const map = new Map();
                suppliersSnapshot.forEach(doc => {
                    const data = doc.data();
                    map.set(doc.id, {
                        id: doc.id,
                        razaoSocial: data.razaoSocial || data.name || '',
                        codigo: data.codigo || '',
                        cnpj: data.cnpj || ''
                    });
                });
                setSuppliersMap(map);
            } catch (error) {
                console.error("Erro ao carregar fornecedores:", error);
            } finally {
                setLoadingSuppliers(false);
            }
        };

        loadSuppliers();
    }, []);

    // Auto-correção de estoque ao carregar a página
    useEffect(() => {
        const runAutoCorrection = async () => {
            try {
                const result = await autoCorrectAllStock();
                if (result.correctedCount > 0) {
                    // Silencioso
                }
            } catch (error) {
                console.warn('Auto-correção falhou:', error);
            }
        };

        runAutoCorrection();
    }, []);

    // Combinar matérias-primas e produtos
    const allItems = useMemo(() => {
        const combined = [];
        if (allRawMaterials) {
            combined.push(...allRawMaterials.map(item => ({ ...item, isProduct: false })));
        }
        if (products) {
            combined.push(...products.map(item => ({ ...item, isProduct: true })));
        }
        return combined;
    }, [allRawMaterials, products]);

    // Mapeamento de todos os itens
    const itemMap = useMemo(() => {
        const map = new Map();
        allItems.forEach(item => {
            map.set(item.id, {
                productTypeIds: item.productTypeIds || [],
                categoryIds: item.categoryIds || [],
                code: item.code,
                name: item.commercialName || item.name,
                isProduct: item.isProduct || false,
                suppliers: item.suppliers || []
            });
        });
        return map;
    }, [allItems]);

    // Debounce para busca
    useEffect(() => {
        const timer = setTimeout(() => {
            setDebouncedSearchTerm(searchTerm);
        }, 300);
        return () => clearTimeout(timer);
    }, [searchTerm]);

    // Reset page quando busca/filtros mudam
    useEffect(() => {
        setPage(1);
    }, [debouncedSearchTerm, filterProductTypeId, filterCategoryId]);

    // Buscar materiais de água (que não estão no current-stock)
    useEffect(() => {
        const fetchWaterMaterials = async () => {
            setLoadingWater(true);
            try {
                const [rawMatsSnapshot, productsSnapshot] = await Promise.all([
                    getDocs(collection(db, RAW_MATERIALS_COLLECTION)),
                    getDocs(collection(db, PRODUCTS_COLLECTION))
                ]);

                const waterItems = [];
                const seenIds = new Set();

                rawMatsSnapshot.forEach(doc => {
                    const data = doc.data();
                    if (isWaterMaterial(data) && !seenIds.has(doc.id)) {
                        seenIds.add(doc.id);
                        waterItems.push({
                            rawMaterialId: doc.id,
                            rawMaterialCode: data.code,
                            rawMaterialName: data.commercialName || data.name,
                            totalQuantity: 0,
                            locations: [{ id: 'INFINITO', description: '', quantity: Infinity }],
                            isWater: true,
                            lotNumber: null,
                            expirationDate: null,
                            suppliers: data.suppliers || [],
                            isProduct: false
                        });
                    }
                });

                productsSnapshot.forEach(doc => {
                    const data = doc.data();
                    if (isWaterMaterial(data) && !seenIds.has(doc.id)) {
                        seenIds.add(doc.id);
                        waterItems.push({
                            rawMaterialId: doc.id,
                            rawMaterialCode: data.code,
                            rawMaterialName: data.commercialName || data.name,
                            totalQuantity: 0,
                            locations: [{ id: 'INFINITO', description: '', quantity: Infinity }],
                            isWater: true,
                            lotNumber: null,
                            expirationDate: null,
                            suppliers: data.suppliers || [],
                            isProduct: true
                        });
                    }
                });

                setWaterMaterials(waterItems);
            } catch (error) {
                console.error("Erro ao buscar materiais de água:", error);
            } finally {
                setLoadingWater(false);
            }
        };

        fetchWaterMaterials();
    }, []);

    // Listener em tempo real para estoque atual (incluindo lote)
    useEffect(() => {
        setLoading(true);

        const q = query(
            collection(db, CURRENT_STOCK_COLLECTION),
            where('quantity', '>', 0.001),
            orderBy('rawMaterialCode')
        );

        const unsubscribe = onSnapshot(q, (querySnapshot) => {
            const stockItemsArray = [];
            querySnapshot.forEach((doc) => {
                const data = doc.data();
                stockItemsArray.push({ id: doc.id, ...data });
            });

            setCurrentStock(stockItemsArray);
            setLoading(false);

            // Auto-corrigir após mudanças no estoque (debounced)
            if (correctionTimeoutRef.current) {
                clearTimeout(correctionTimeoutRef.current);
            }

            correctionTimeoutRef.current = setTimeout(() => {
                autoCorrectStockAfterChange(stockItemsArray);
            }, 2000);

            setStatus({
                message: t('pages.stock.query.stockUpdated', 'Estoque atualizado: {time}', { time: new Date().toLocaleTimeString() }),
                type: 'info'
            });

            setTimeout(() => {
                setStatus(prev => prev.type === 'info' ? { message: '', type: '' } : prev);
            }, 2000);
        }, (error) => {
            console.error("Erro no Listener:", error);
            setLoading(false);
            setStatus({ message: t('pages.stock.query.realtimeError', 'Erro na conexão em tempo real'), type: 'error' });
        });

        return () => {
            unsubscribe();
            if (correctionTimeoutRef.current) {
                clearTimeout(correctionTimeoutRef.current);
            }
        };
    }, [t]);

    // Função para auto-corrigir estoque após mudanças
    const autoCorrectStockAfterChange = useCallback(async (stockItems) => {
        if (!stockItems || stockItems.length === 0) return;

        try {
            const materials = new Map();
            stockItems.forEach(item => {
                const key = `${item.rawMaterialId}_${item.lotNumber || ''}`;
                if (!materials.has(key)) {
                    materials.set(key, {
                        rawMaterialId: item.rawMaterialId,
                        lotNumber: item.lotNumber || null
                    });
                }
            });

            let correctedCount = 0;
            for (const [key, material] of materials) {
                const lastCorrection = lastCorrectionRef.current[key];
                if (lastCorrection && Date.now() - lastCorrection < 10000) {
                    continue;
                }

                try {
                    const result = await autoCorrectStockInconsistency(
                        material.rawMaterialId,
                        material.lotNumber
                    );

                    if (result && result.corrected) {
                        correctedCount++;
                        lastCorrectionRef.current[key] = Date.now();
                    }
                } catch (error) {
                    // Silenciar erros de correção
                }
            }

            if (correctedCount > 0) {
                // Silencioso
            }
        } catch (error) {
            // Silenciar erros
        }
    }, []);

    // Carregar status de validade salvos do Firestore
    useEffect(() => {
        const loadExpiryStatuses = async () => {
            try {
                const stockSnapshot = await getDocs(collection(db, CURRENT_STOCK_COLLECTION));
                const statusMap = new Map();

                stockSnapshot.forEach(doc => {
                    const data = doc.data();
                    if (data.expiryStatus) {
                        const key = `${data.rawMaterialId}_${data.lotNumber || ''}`;
                        if (!statusMap.has(key) || data.updatedAt?.seconds > statusMap.get(key)?.timestamp) {
                            statusMap.set(key, {
                                status: data.expiryStatus,
                                timestamp: data.updatedAt?.seconds || 0,
                                reviewedBy: data.reviewedBy || null,
                                reviewDate: data.reviewDate || null,
                                reviewNotes: data.reviewNotes || null
                            });
                        }
                    }
                });

                const simpleStatusMap = new Map();
                statusMap.forEach((value, key) => {
                    simpleStatusMap.set(key, value.status);
                });

                setExpiryStatusMap(simpleStatusMap);
            } catch (error) {
                console.error('Erro ao carregar status de validade:', error);
            }
        };

        loadExpiryStatuses();
    }, []);

    // Helper function to get supplier names
    const getSupplierNames = useCallback((item) => {
        if (item.isWater) return '—';

        if (item.isProduct === true) {
            return t('pages.stock.query.internalProduction', 'Produção Própria');
        }

        if (item.suppliers && item.suppliers.length > 0) {
            const names = item.suppliers
                .map(s => s.razaoSocial || s.name || '')
                .filter(Boolean);
            return names.length > 0 ? names.join(', ') : '—';
        }

        if (item.supplierIds && item.supplierIds.length > 0) {
            const names = item.supplierIds
                .map(id => suppliersMap.get(id)?.razaoSocial || '')
                .filter(Boolean);
            return names.length > 0 ? names.join(', ') : '—';
        }

        const itemData = itemMap.get(item.rawMaterialId);
        if (itemData) {
            if (itemData.isProduct === true) {
                return t('pages.stock.query.internalProduction', 'Produção Própria');
            }

            if (itemData.suppliers && itemData.suppliers.length > 0) {
                const names = itemData.suppliers
                    .map(s => s.razaoSocial || s.name || '')
                    .filter(Boolean);
                return names.length > 0 ? names.join(', ') : '—';
            }
        }

        return '—';
    }, [itemMap, suppliersMap, t]);

    // Agrupamento de estoque (incluindo informações de lote e fornecedores)
    const groupedStock = useMemo(() => {
        const physicalStockMap = new Map();

        if (currentStock && currentStock.length > 0) {
            currentStock.forEach(item => {
                const key = item.lotNumber
                    ? `${item.rawMaterialId}_${item.lotNumber}`
                    : `${item.rawMaterialId}`;

                const itemData = itemMap.get(item.rawMaterialId);
                const suppliers = itemData?.suppliers || [];
                const isProduct = item.isProduct || itemData?.isProduct || false;

                if (!physicalStockMap.has(key)) {
                    physicalStockMap.set(key, {
                        rawMaterialId: item.rawMaterialId,
                        rawMaterialCode: item.rawMaterialCode,
                        rawMaterialName: item.rawMaterialName,
                        totalQuantity: 0,
                        locations: [],
                        isWater: false,
                        isProduct: isProduct,
                        lotNumber: item.lotNumber || null,
                        expirationDate: item.expirationDate || null,
                        manufacturerDate: item.manufacturerDate || null,
                        stockIds: [],
                        suppliers: suppliers
                    });
                }

                const group = physicalStockMap.get(key);
                group.totalQuantity += item.quantity;
                group.locations.push({
                    id: item.locationId,
                    description: item.locationDescription,
                    quantity: item.quantity,
                    stockId: item.id,
                    lotNumber: item.lotNumber,
                    expirationDate: item.expirationDate,
                });
                group.stockIds.push(item.id);
            });
        }

        for (const group of physicalStockMap.values()) {
            group.locations.sort((a, b) => a.id.localeCompare(b.id));
        }

        const existingStockIds = new Set();
        for (const group of physicalStockMap.values()) {
            existingStockIds.add(group.rawMaterialId);
        }

        const productsNotInStock = [];
        if (products && products.length > 0) {
            products.forEach(product => {
                if (!existingStockIds.has(product.id) && !isWaterMaterial(product)) {
                    productsNotInStock.push({
                        rawMaterialId: product.id,
                        rawMaterialCode: product.code,
                        rawMaterialName: product.commercialName || product.name,
                        totalQuantity: 0,
                        locations: [{
                            id: 'SEM ESTOQUE',
                            description: t('pages.stock.query.noEntry', 'Produto sem entrada registrada'),
                            quantity: 0,
                            stockId: 'none'
                        }],
                        isWater: false,
                        isProduct: true,
                        lotNumber: null,
                        expirationDate: null,
                        stockIds: [],
                        suppliers: product.suppliers || []
                    });
                }
            });
        }

        const rawMaterialsNotInStock = [];
        if (allRawMaterials && allRawMaterials.length > 0) {
            allRawMaterials.forEach(rm => {
                if (!existingStockIds.has(rm.id) && !isWaterMaterial(rm)) {
                    rawMaterialsNotInStock.push({
                        rawMaterialId: rm.id,
                        rawMaterialCode: rm.code,
                        rawMaterialName: rm.commercialName || rm.name,
                        totalQuantity: 0,
                        locations: [{
                            id: 'SEM ESTOQUE',
                            description: t('pages.stock.query.noEntry', 'Material sem entrada registrada'),
                            quantity: 0,
                            stockId: 'none'
                        }],
                        isWater: false,
                        isProduct: false,
                        lotNumber: null,
                        expirationDate: null,
                        stockIds: [],
                        suppliers: rm.suppliers || []
                    });
                }
            });
        }

        const physicalList = Array.from(physicalStockMap.values());
        const waterList = [...waterMaterials];
        const combined = [...physicalList, ...productsNotInStock, ...rawMaterialsNotInStock, ...waterList];

        combined.sort((a, b) => (a.rawMaterialCode || '').localeCompare(b.rawMaterialCode || ''));

        return combined;
    }, [currentStock, waterMaterials, products, allRawMaterials, itemMap, t]);

    // Filtragem
    const filteredStock = useMemo(() => {
        if (!groupedStock) return [];
        if (!debouncedSearchTerm && !filterProductTypeId && !filterCategoryId) {
            return groupedStock;
        }

        const lowerCaseSearchTerm = debouncedSearchTerm.toLowerCase();

        return groupedStock.filter(item => {
            const locationMatch = item.locations.some(loc =>
                loc.id?.toLowerCase().includes(lowerCaseSearchTerm) ||
                loc.description?.toLowerCase().includes(lowerCaseSearchTerm)
            );

            const lotMatch = item.lotNumber?.toLowerCase().includes(lowerCaseSearchTerm) || false;

            let supplierMatch = false;
            if (item.isProduct === true) {
                supplierMatch = lowerCaseSearchTerm.includes('produção') ||
                    lowerCaseSearchTerm.includes('propria') ||
                    lowerCaseSearchTerm.includes('própria') ||
                    lowerCaseSearchTerm.includes('interno');
            } else {
                supplierMatch = item.suppliers?.some(s =>
                    (s.razaoSocial || s.name || '').toLowerCase().includes(lowerCaseSearchTerm)
                ) || false;
            }

            const matchesSearchTerm = !debouncedSearchTerm || (
                (item.rawMaterialCode || '').toLowerCase().includes(lowerCaseSearchTerm) ||
                (item.rawMaterialName || '').toLowerCase().includes(lowerCaseSearchTerm) ||
                locationMatch ||
                lotMatch ||
                supplierMatch
            );

            if (!matchesSearchTerm) return false;

            if (item.isWater) return true;

            if (item.locations.some(loc => loc.id === 'SEM ESTOQUE')) {
                const itemProperties = itemMap.get(item.rawMaterialId);
                if (!itemProperties) return !filterProductTypeId && !filterCategoryId;

                const matchesProductType = !filterProductTypeId ||
                    (itemProperties.productTypeIds?.includes(filterProductTypeId) ?? false);
                const matchesCategory = !filterCategoryId ||
                    (itemProperties.categoryIds?.includes(filterCategoryId) ?? false);
                return matchesProductType && matchesCategory;
            }

            const itemProperties = itemMap.get(item.rawMaterialId);
            if (!itemProperties) return !filterProductTypeId && !filterCategoryId;

            const matchesProductType = !filterProductTypeId ||
                (itemProperties.productTypeIds?.includes(filterProductTypeId) ?? false);

            const matchesCategory = !filterCategoryId ||
                (itemProperties.categoryIds?.includes(filterCategoryId) ?? false);

            return matchesProductType && matchesCategory;
        });
    }, [groupedStock, debouncedSearchTerm, filterProductTypeId, filterCategoryId, itemMap]);

    const totalFilteredItems = filteredStock.length;
    const totalPages = Math.ceil(totalFilteredItems / ITEMS_PER_PAGE);

    const currentStockPage = useMemo(() => {
        const startIndex = (page - 1) * ITEMS_PER_PAGE;
        const endIndex = startIndex + ITEMS_PER_PAGE;
        return filteredStock.slice(startIndex, endIndex);
    }, [filteredStock, page]);

    const handlePageChange = (newPage) => {
        if (newPage > 0 && newPage <= totalPages) {
            setPage(newPage);
        }
    };

    // Função para buscar histórico com onSnapshot (incluindo lote)
    const fetchHistoryWithLiveUpdates = useCallback((rawMaterialId, locationIdsArray, isWater = false, lotNumber = null) => {
        if (historyUnsubscribe) {
            historyUnsubscribe();
            setHistoryUnsubscribe(null);
        }

        setHistoryLoading(true);
        setSelectedItemHistory([]);

        let entriesQuery;
        let outflowsQuery;

        if (lotNumber && !isWater) {
            entriesQuery = query(
                collection(db, STOCK_ENTRIES_COLLECTION),
                where("rawMaterialId", "==", rawMaterialId),
                where("lotNumber", "==", lotNumber),
                orderBy('createdAt', 'desc')
            );

            outflowsQuery = query(
                collection(db, STOCK_OUTFLOWS_COLLECTION),
                where("rawMaterialId", "==", rawMaterialId),
                where("lotNumber", "==", lotNumber),
                orderBy('createdAt', 'desc')
            );
        } else {
            entriesQuery = query(
                collection(db, STOCK_ENTRIES_COLLECTION),
                where("rawMaterialId", "==", rawMaterialId),
                orderBy('createdAt', 'desc')
            );

            outflowsQuery = query(
                collection(db, STOCK_OUTFLOWS_COLLECTION),
                where("rawMaterialId", "==", rawMaterialId),
                orderBy('createdAt', 'desc')
            );
        }

        let entriesData = [];
        let outflowsData = [];
        let isProcessing = false;

        const processAndMerge = () => {
            if (isProcessing) return;
            isProcessing = true;

            const allMovements = [];

            entriesData.forEach(item => {
                const data = item.data;
                const docId = item.id;

                if (isWater || data.isWater) {
                    const qty = Number(data.quantity) || 0;
                    if (qty > 0) {
                        allMovements.push({
                            id: docId,
                            type: 'ENTRADA',
                            createdAt: data.createdAt,
                            updatedAt: data.updatedAt,
                            createdBy: data.createdBy,
                            updatedBy: data.updatedBy,
                            notes: data.notes,
                            displayQuantity: qty,
                            isWater: true,
                            lotNumber: data.lotNumber || null,
                            expirationDate: data.expirationDate || null,
                        });
                    }
                } else {
                    let totalQty = 0;
                    if (data.locationQuantities && typeof data.locationQuantities === 'object') {
                        totalQty = Object.values(data.locationQuantities).reduce((acc, qty) => {
                            return acc + (typeof qty === 'number' ? qty : (Number(qty) || 0));
                        }, 0);
                    } else if (data.quantity !== undefined && data.quantity !== null) {
                        totalQty = Number(data.quantity) || 0;
                    }

                    if (totalQty > 0) {
                        allMovements.push({
                            id: docId,
                            type: data.type === 'TRANSFER_IN' ? 'TRANSFER_IN' : 'ENTRADA',
                            createdAt: data.createdAt,
                            updatedAt: data.updatedAt,
                            createdBy: data.createdBy,
                            updatedBy: data.updatedBy,
                            notes: data.notes,
                            displayQuantity: totalQty,
                            isWater: false,
                            lotNumber: data.lotNumber || null,
                            expirationDate: data.expirationDate || null,
                        });
                    }
                }
            });

            outflowsData.forEach(item => {
                const data = item.data;
                const docId = item.id;

                if (isWater || data.isWater) {
                    const qty = Number(data.quantity) || 0;
                    if (qty > 0) {
                        allMovements.push({
                            id: docId,
                            type: 'SAÍDA',
                            createdAt: data.createdAt,
                            updatedAt: data.updatedAt,
                            createdBy: data.createdBy,
                            updatedBy: data.updatedBy,
                            notes: data.notes,
                            displayQuantity: qty,
                            isWater: true,
                            lotNumber: data.lotNumber || null,
                            expirationDate: data.expirationDate || null,
                        });
                    }
                } else {
                    let totalQty = 0;
                    if (data.locationQuantities && typeof data.locationQuantities === 'object') {
                        totalQty = Object.values(data.locationQuantities).reduce((acc, qty) => {
                            return acc + (typeof qty === 'number' ? qty : (Number(qty) || 0));
                        }, 0);
                    } else if (data.quantity !== undefined && data.quantity !== null) {
                        totalQty = Number(data.quantity) || 0;
                    }

                    if (totalQty > 0) {
                        allMovements.push({
                            id: docId,
                            type: data.type === 'TRANSFER_OUT' ? 'TRANSFER_OUT' : 'SAÍDA',
                            createdAt: data.createdAt,
                            updatedAt: data.updatedAt,
                            createdBy: data.createdBy,
                            updatedBy: data.updatedBy,
                            notes: data.notes,
                            displayQuantity: totalQty,
                            isWater: false,
                            lotNumber: data.lotNumber || null,
                            expirationDate: data.expirationDate || null,
                        });
                    }
                }
            });

            allMovements.sort((a, b) => {
                const dateA = a.createdAt?.seconds || 0;
                const dateB = b.createdAt?.seconds || 0;
                return dateB - dateA;
            });

            setSelectedItemHistory(allMovements);
            setHistoryLoading(false);
            isProcessing = false;
        };

        const unsubscribeEntries = onSnapshot(entriesQuery, (snapshot) => {
            entriesData = snapshot.docs.map(doc => ({
                id: doc.id,
                data: doc.data()
            }));
            processAndMerge();
        }, (error) => {
            console.error("Erro no listener de entradas:", error);
            setHistoryLoading(false);
        });

        const unsubscribeOutflows = onSnapshot(outflowsQuery, (snapshot) => {
            outflowsData = snapshot.docs.map(doc => ({
                id: doc.id,
                data: doc.data()
            }));
            processAndMerge();
        }, (error) => {
            console.error("Erro no listener de saídas:", error);
            setHistoryLoading(false);
        });

        const combinedUnsubscribe = () => {
            unsubscribeEntries();
            unsubscribeOutflows();
        };

        setHistoryUnsubscribe(() => combinedUnsubscribe);

        const timeoutId = setTimeout(() => {
            if (historyLoading) {
                setHistoryLoading(false);
                setStatus({ message: t('pages.stock.query.historyTimeout', 'Timeout ao carregar histórico'), type: 'error' });
            }
        }, 10000);

        return () => {
            clearTimeout(timeoutId);
        };
    }, [historyUnsubscribe, historyLoading, t]);

    // Abrir histórico (com suporte a lote)
    const handleRowClick = useCallback(async (groupedItem) => {
        let locationInfo = '';
        if (groupedItem.isWater) {
            locationInfo = t('pages.stock.query.noLocation', 'Sem localização');
        } else {
            const allLocationNames = groupedItem.locations
                .slice(0, 5)
                .map(loc => `${loc.id} - ${loc.description}`)
                .join(', ');
            locationInfo = groupedItem.locations.length > 5
                ? `${allLocationNames} ${t('pages.stock.query.andMore', 'e mais {count}', { count: groupedItem.locations.length - 5 })}`
                : allLocationNames;
        }

        setSelectedItemDetails({
            code: groupedItem.rawMaterialCode,
            name: groupedItem.rawMaterialName,
            location: locationInfo,
            isWater: groupedItem.isWater || false
        });

        setIsHistoryModalOpen(true);

        const allLocationIds = groupedItem.isWater ? [] : groupedItem.locations.map(loc => loc.id);
        fetchHistoryWithLiveUpdates(
            groupedItem.rawMaterialId,
            allLocationIds,
            groupedItem.isWater,
            groupedItem.lotNumber
        );
    }, [fetchHistoryWithLiveUpdates, t]);

    // Fechar histórico
    const closeHistoryModal = useCallback(() => {
        if (historyUnsubscribe) {
            historyUnsubscribe();
            setHistoryUnsubscribe(null);
        }
        setIsHistoryModalOpen(false);
        setSelectedItemDetails(null);
        setSelectedItemHistory([]);
        setHistoryLoading(false);
    }, [historyUnsubscribe]);

    const handleFilterChange = (setter, value) => {
        setter(value);
        setPage(1);
    };

    const handleTransferClick = (e, groupedItem) => {
        e.stopPropagation();
        if (groupedItem.isWater) return;
        const itemWithLot = {
            ...groupedItem,
            lotNumber: groupedItem.lotNumber,
            expirationDate: groupedItem.expirationDate,
            manufacturerDate: groupedItem.manufacturerDate,
            isProduct: groupedItem.isProduct
        };
        setSelectedItemForTransfer(itemWithLot);
        setIsMovementModalOpen(true);
    };

    const closeMovementModal = () => {
        setIsMovementModalOpen(false);
        setSelectedItemForTransfer(null);
    };

    // Função para atualizar o status de validade de um item no Firestore (persistente)
    const updateExpiryStatus = async (item, newStatus, reviewNotes = '') => {
        try {
            let stockQuery;
            if (item.lotNumber) {
                stockQuery = query(
                    collection(db, CURRENT_STOCK_COLLECTION),
                    where("rawMaterialId", "==", item.rawMaterialId),
                    where("lotNumber", "==", item.lotNumber),
                    where("quantity", ">", 0.001)
                );
            } else {
                stockQuery = query(
                    collection(db, CURRENT_STOCK_COLLECTION),
                    where("rawMaterialId", "==", item.rawMaterialId),
                    where("quantity", ">", 0.001)
                );
            }

            const snapshot = await getDocs(stockQuery);

            if (snapshot.empty) {
                setStatus({
                    message: t('pages.stock.query.noStockFound', 'Nenhum registro de estoque encontrado para este material/lote'),
                    type: 'error'
                });
                return;
            }

            setUpdatingStatus(true);
            const batch = writeBatch(db);
            const now = new Date();
            let updatedCount = 0;

            snapshot.forEach((doc) => {
                const stockRef = doc.ref;
                const updateData = {
                    expiryStatus: newStatus,
                    updatedAt: now,
                    reviewedAt: now,
                    reviewedBy: 'Usuário'
                };

                if (reviewNotes) {
                    updateData.reviewNotes = reviewNotes;
                }

                batch.update(stockRef, updateData);
                updatedCount++;
            });

            await batch.commit();

            setExpiryStatusMap(prev => {
                const newMap = new Map(prev);
                const keyPrefix = `${item.rawMaterialId}_${item.lotNumber || ''}`;
                for (const [key] of newMap) {
                    if (key.startsWith(keyPrefix) || key === keyPrefix) {
                        newMap.set(key, newStatus);
                    }
                }
                const mainKey = `${item.rawMaterialId}_${item.lotNumber || ''}`;
                newMap.set(mainKey, newStatus);
                return newMap;
            });

            const statusConfig = EXPIRY_STATUS_CONFIG[newStatus];
            setStatus({
                message: t('pages.stock.query.statusUpdated', 'Status atualizado para: {status} ({count} registros em {total} localizações)', {
                    status: statusConfig?.label || newStatus,
                    count: updatedCount,
                    total: snapshot.size
                }),
                type: 'success'
            });

            setTimeout(() => {
                setStatus(prev => prev.type === 'success' ? { message: '', type: '' } : prev);
            }, 3000);

        } catch (error) {
            console.error('Erro ao atualizar status:', error);
            setStatus({ message: t('pages.stock.query.statusUpdateError', 'Erro ao atualizar status. Tente novamente.'), type: 'error' });
        } finally {
            setUpdatingStatus(false);
        }
    };

    const cycleExpiryStatus = async (e, item) => {
        e.stopPropagation();

        if (item.isWater || !item.expirationDate) return;

        const mainKey = `${item.rawMaterialId}_${item.lotNumber || ''}`;
        const currentStatus = expiryStatusMap.get(mainKey);

        if (!currentStatus || currentStatus === EXPIRY_STATUS.EXPIRED) {
            await updateExpiryStatus(item, EXPIRY_STATUS.PENDING_REVIEW);
        }
        else if (currentStatus === EXPIRY_STATUS.PENDING_REVIEW) {
            await updateExpiryStatus(item, EXPIRY_STATUS.EXPIRED);
        }
        else {
            await updateExpiryStatus(item, EXPIRY_STATUS.EXPIRED);
        }
    };

    const getItemExpiryStatus = useCallback((item) => {
        if (item.isWater) return null;
        if (!item.expirationDate) return null;
        if (!isExpired(item.expirationDate)) return null;

        const mainKey = `${item.rawMaterialId}_${item.lotNumber || ''}`;
        const savedStatus = expiryStatusMap.get(mainKey);

        return savedStatus || EXPIRY_STATUS.EXPIRED;
    }, [expiryStatusMap]);

    const renderExpiryTag = (item, status) => {
        if (!status) return null;

        const config = EXPIRY_STATUS_CONFIG[status];
        if (!config) return null;

        const statusLabels = {
            [EXPIRY_STATUS.EXPIRED]: t('pages.stock.query.statusExpired', 'Vencido'),
            [EXPIRY_STATUS.PENDING_REVIEW]: t('pages.stock.query.statusPendingReview', 'Aguardando Análise'),
            [EXPIRY_STATUS.APPROVED]: t('pages.stock.query.statusApproved', 'Aprovado'),
            [EXPIRY_STATUS.REJECTED]: t('pages.stock.query.statusRejected', 'Rejeitado'),
            [EXPIRY_STATUS.REPROCESSED]: t('pages.stock.query.statusReprocessed', 'Reprocessado')
        };

        return (
            <span
                className={styles.tagSmall}
                style={{
                    backgroundColor: config.bgColor,
                    color: config.color,
                    marginLeft: '5px',
                    padding: '2px 8px',
                    borderRadius: '3px',
                    fontSize: '11px',
                    fontWeight: 'bold',
                    display: 'inline-block'
                }}
                title={status === EXPIRY_STATUS.PENDING_REVIEW ? t('pages.stock.query.pendingReviewTooltip', 'Aguardando análise - Clique no botão ⏳ para alterar') : t('pages.stock.query.statusTooltip', 'Clique no botão para alterar o status')}
            >
                {config.icon} {statusLabels[status] || config.label}
            </span>
        );
    };

    const lowStockItems = useMemo(() => {
        return groupedStock.filter(item => !item.isWater && item.totalQuantity < 500 && item.totalQuantity > 0);
    }, [groupedStock]);

    const isLoading = loading || loadingRawMaterials || loadingProducts || loadingTypes || loadingCategories || loadingWater;

    const handleSearch = (term) => {
        setSearchTerm(term);
        setPage(1);
    };

    return (
        <div className={styles.pageContainer}>
            <ItemHistoryModal
                isOpen={isHistoryModalOpen}
                onClose={closeHistoryModal}
                itemDetails={selectedItemDetails}
                history={selectedItemHistory}
                loading={historyLoading}
                onRetry={() => {
                    if (selectedItemDetails) {
                        const item = groupedStock.find(i => i.rawMaterialCode === selectedItemDetails.code);
                        if (item) {
                            const locationIds = item.isWater ? [] : item.locations.map(loc => loc.id);
                            fetchHistoryWithLiveUpdates(item.rawMaterialId, locationIds, item.isWater, item.lotNumber);
                        }
                    }
                }}
                t={t}
            />

            <LowStockModal
                isOpen={isLowStockModalOpen}
                onClose={() => setIsLowStockModalOpen(false)}
                items={lowStockItems}
                loading={isLoading}
                t={t}
            />

            <StockMovementModal
                isOpen={isMovementModalOpen}
                onClose={closeMovementModal}
                selectedItem={selectedItemForTransfer}
            />

            {/* Page Header */}
            <PageHeader
                title={t('pages.stock.query.title', 'Consulta de Estoque')}
                actionText={can.view ? t('pages.stock.query.lowStock', 'Estoque Baixo') : undefined}
                onAction={can.view ? () => setIsLowStockModalOpen(true) : undefined}
                disabled={isLoading}
                showAction={!!can.view}
            />

            {/* Status Toast */}
            {status.message && (
                <Toast
                    message={status.message}
                    type={status.type}
                    onClose={() => setStatus({ message: '', type: '' })}
                />
            )}

            {/* Área de Busca e Filtros */}
            <div className={styles.searchBar} style={{ display: 'flex', gap: '20px', alignItems: 'flex-end', marginBottom: '15px', padding: '0', flexWrap: 'wrap' }}>
                <div className={styles.inputGroup} style={{ flex: '1', minWidth: '200px' }}>
                    <label htmlFor="listSearch">{t('pages.stock.query.searchLabel', 'Buscar (Código, Nome, Lote, Localização ou Fornecedor)')}</label>
                    <SearchBar
                        placeholder={t('pages.stock.query.searchPlaceholder', 'Digite para buscar...')}
                        onSearch={handleSearch}
                        disabled={isLoading}
                        debounceDelay={300}
                    />
                </div>

                <div className={styles.inputGroup} style={{ flex: '0 0 200px' }}>
                    <label htmlFor="filterType">{t('pages.stock.query.filterByType', 'Filtrar por Tipo')}</label>
                    <select
                        id="filterType"
                        value={filterProductTypeId}
                        onChange={(e) => handleFilterChange(setFilterProductTypeId, e.target.value)}
                        className={`${styles.input} ${styles.select}`}
                        disabled={loadingTypes || isLoading}
                    >
                        <option value="">{t('pages.stock.query.allTypes', 'Todos os Tipos')}</option>
                        {productTypes && productTypes.map(type => (
                            <option key={type.id} value={type.id}>{type.name}</option>
                        ))}
                    </select>
                </div>

                <div className={styles.inputGroup} style={{ flex: '0 0 200px' }}>
                    <label htmlFor="filterCategory">{t('pages.stock.query.filterByCategory', 'Filtrar por Categoria')}</label>
                    <select
                        id="filterCategory"
                        value={filterCategoryId}
                        onChange={(e) => handleFilterChange(setFilterCategoryId, e.target.value)}
                        className={`${styles.input} ${styles.select}`}
                        disabled={loadingCategories || isLoading}
                    >
                        <option value="">{t('pages.stock.query.allCategories', 'Todas as Categorias')}</option>
                        {categories && categories.map(category => (
                            <option key={category.id} value={category.id}>{category.name}</option>
                        ))}
                    </select>
                </div>
            </div>

            {/* Tabela de Saldo de Estoque */}
            <div className={styles.tableCard}>
                <div className={styles.tableContainer}>
                    <table className={styles.table}>
                        <thead>
                            <tr>
                                <th style={{ width: '100px' }}>{t('pages.stock.query.tableCode', 'Código')}</th>
                                <th>{t('pages.stock.query.tableItem', 'Item')}</th>
                                <th style={{ width: '150px' }}>{t('pages.stock.query.tableSupplier', 'Fornecedor')}</th>
                                <th style={{ width: '100px' }}>{t('pages.stock.query.tableLot', 'Lote')}</th>
                                <th style={{ width: '100px' }}>{t('pages.stock.query.tableExpiration', 'Validade')}</th>
                                <th style={{ width: '250px' }}>{t('pages.stock.query.tableLocations', 'Localizações')}</th>
                                <th style={{ width: '150px', textAlign: 'right' }}>{t('pages.stock.query.tableBalance', 'Saldo Total')}</th>
                                <th style={{ width: '150px', textAlign: 'center' }}>{t('pages.stock.query.tableActions', 'Ações')}</th>
                            </tr>
                        </thead>
                        <tbody>
                            {isLoading && currentStock.length === 0 && waterMaterials.length === 0 ? (
                                <TableSkeleton columns={8} rows={5} />
                            ) : (
                                currentStockPage.map((item) => {
                                    const isWater = item.isWater === true;
                                    const isNoStock = item.locations.some(loc => loc.id === 'SEM ESTOQUE');
                                    const expiryStatus = getItemExpiryStatus(item);
                                    const isExpiredItem = expiryStatus === EXPIRY_STATUS.EXPIRED;
                                    const isPendingReview = expiryStatus === EXPIRY_STATUS.PENDING_REVIEW;
                                    const supplierNames = getSupplierNames(item);

                                    return (
                                        <tr
                                            key={`${item.rawMaterialId}_${item.lotNumber || ''}_${item.locations[0]?.id || ''}`}
                                            className={`${styles.tableRow} ${styles.clickableRow}`}
                                            onClick={() => handleRowClick(item)}
                                            title={t('pages.stock.query.clickForHistory', 'Clique para ver o histórico')}
                                            style={{
                                                cursor: 'pointer',
                                                opacity: isNoStock && item.totalQuantity === 0 ? 0.7 : 1,
                                                backgroundColor: isExpiredItem ? '#fff5f5' :
                                                    isPendingReview ? '#fffbeb' : undefined,
                                                borderLeft: isExpiredItem ? '4px solid #e53e3e' :
                                                    isPendingReview ? '4px solid #ed8936' : undefined
                                            }}
                                        >
                                            <td style={{ fontWeight: 'bold' }}>{item.rawMaterialCode}</td>
                                            <td>
                                                {item.rawMaterialName}
                                                {renderExpiryTag(item, expiryStatus)}
                                            </td>
                                            <td>
                                                {isWater ? '—' : (
                                                    <span style={{
                                                        fontSize: '0.85rem',
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        gap: '4px'
                                                    }}>
                                                        {item.isProduct === true ? (
                                                            <span style={{
                                                                backgroundColor: '#e8f5e9',
                                                                color: '#2e7d32',
                                                                padding: '2px 8px',
                                                                borderRadius: '12px',
                                                                fontWeight: '500',
                                                                fontSize: '0.75rem',
                                                                display: 'inline-flex',
                                                                alignItems: 'center',
                                                                gap: '4px'
                                                            }}>
                                                                {t('pages.stock.query.internalProduction', 'Produção Própria')}
                                                            </span>
                                                        ) : (
                                                            supplierNames
                                                        )}
                                                    </span>
                                                )}
                                            </td>
                                            <td>
                                                {isWater ? '—' : (item.lotNumber || '—')}
                                            </td>
                                            <td>
                                                {isWater ? '—' : (item.expirationDate ? formatDate(item.expirationDate) : '—')}
                                            </td>
                                            <td>
                                                {isWater ? (
                                                    <span className={styles.tagSmall} style={{ backgroundColor: '#e3f2fd', color: '#1565c0' }}>
                                                        {t('pages.stock.query.infiniteResource', 'Recurso Infinito')}
                                                    </span>
                                                ) : (
                                                    <>
                                                        {item.locations.slice(0, 3).map(loc => (
                                                            <span
                                                                key={loc.stockId || loc.id}
                                                                className={styles.tagSmall}
                                                                style={{
                                                                    marginRight: '5px',
                                                                    backgroundColor: loc.id === 'SEM ESTOQUE' ? '#f0f0f0' : 'var(--secondary-color)',
                                                                    color: loc.id === 'SEM ESTOQUE' ? '#666' : 'white',
                                                                    display: 'inline-block',
                                                                    lineHeight: '1.2',
                                                                    padding: '2px 5px'
                                                                }}
                                                                title={`${loc.description}: ${loc.quantity.toFixed(3)}${loc.lotNumber ? ` | ${t('pages.stock.query.lotLabel', 'Lote')}: ${loc.lotNumber}` : ''}`}
                                                            >
                                                                {loc.id}
                                                            </span>
                                                        ))}
                                                        {item.locations.length > 3 && (
                                                            <span className={styles.tagSmall} style={{ marginRight: '5px' }}>
                                                                +{item.locations.length - 3}
                                                            </span>
                                                        )}
                                                    </>
                                                )}
                                            </td>
                                            <td style={{ fontWeight: 'bold', textAlign: 'right' }}>
                                                {isWater ? '∞ (Ilimitado)' : (
                                                    <span>
                                                        {item.totalQuantity.toLocaleString('pt-BR', {
                                                            minimumFractionDigits: 3,
                                                            maximumFractionDigits: 3
                                                        })}
                                                    </span>
                                                )}
                                            </td>
                                            <td style={{ textAlign: 'center' }} className={styles.actionCell} onClick={(e) => e.stopPropagation()}>
                                                <div style={{ display: 'flex', gap: '8px', justifyContent: 'center', alignItems: 'center', flexWrap: 'wrap' }}>
                                                    {can.print && (() => {
                                                        const isWater = item.isWater === true;

                                                        if (isWater) {
                                                            return (
                                                                <PDFDownloadLink
                                                                    document={<LabelDocument item={{
                                                                        rawMaterialCode: item.rawMaterialCode,
                                                                        rawMaterialName: item.rawMaterialName,
                                                                        lotNumber: 'N/A',
                                                                        manufacturerDate: null,
                                                                        expirationDate: null,
                                                                        expiryStatus: null,
                                                                        isWater: true
                                                                    }} />}
                                                                    fileName={`etiqueta_${item.rawMaterialCode || 'item'}.pdf`}
                                                                    className={`${styles.actionButton} ${styles.printButton}`}
                                                                    title={t('pages.stock.query.printLabel', 'Imprimir Placa')}
                                                                >
                                                                    <FiPrinter size={18} />
                                                                </PDFDownloadLink>
                                                            );
                                                        }

                                                        const hasLot = item.lotNumber && item.lotNumber !== '';
                                                        const hasManufacturerDate = item.manufacturerDate !== null && item.manufacturerDate !== undefined;
                                                        const hasExpirationDate = item.expirationDate !== null && item.expirationDate !== undefined;

                                                        let hasSupplier = false;
                                                        if (item.isProduct === true) {
                                                            hasSupplier = true;
                                                        } else {
                                                            hasSupplier = (item.suppliers && item.suppliers.length > 0) ||
                                                                (item.supplierIds && item.supplierIds.length > 0);

                                                            if (!hasSupplier) {
                                                                const itemData = itemMap.get(item.rawMaterialId);
                                                                hasSupplier = itemData &&
                                                                    ((itemData.suppliers && itemData.suppliers.length > 0) ||
                                                                        (itemData.supplierIds && itemData.supplierIds.length > 0));
                                                            }
                                                        }

                                                        const canPrint = hasLot && hasManufacturerDate && hasExpirationDate && hasSupplier;

                                                        if (!canPrint) {
                                                            let missingInfo = [];
                                                            if (!hasLot) missingInfo.push('Lote');
                                                            if (!hasManufacturerDate) missingInfo.push('Fabricação');
                                                            if (!hasExpirationDate) missingInfo.push('Validade');
                                                            if (!hasSupplier && item.isProduct !== true) missingInfo.push('Fornecedor');

                                                            const tooltipText = `Informações incompletas: ${missingInfo.join(', ')}`;

                                                            return (
                                                                <button
                                                                    className={`${styles.actionButton} ${styles.printButtonDisabled}`}
                                                                    disabled={true}
                                                                    style={{
                                                                        opacity: 0.5,
                                                                        cursor: 'not-allowed',
                                                                        backgroundColor: '#e2e8f0',
                                                                        color: '#a0aec0',
                                                                        border: '1px solid #cbd5e0',
                                                                        borderRadius: '8px',
                                                                        width: '36px',
                                                                        height: '36px',
                                                                        display: 'inline-flex',
                                                                        alignItems: 'center',
                                                                        justifyContent: 'center'
                                                                    }}
                                                                    title={tooltipText}
                                                                >
                                                                    <FiPrinter size={18} />
                                                                </button>
                                                            );
                                                        }

                                                        return (
                                                            <PDFDownloadLink
                                                                document={<LabelDocument item={{
                                                                    rawMaterialCode: item.rawMaterialCode,
                                                                    rawMaterialName: item.rawMaterialName,
                                                                    lotNumber: item.lotNumber || 'N/A',
                                                                    manufacturerDate: item.manufacturerDate || null,
                                                                    expirationDate: item.expirationDate || null,
                                                                    expiryStatus: expiryStatus,
                                                                    isWater: false
                                                                }} />}
                                                                fileName={`etiqueta_${item.rawMaterialCode || 'item'}.pdf`}
                                                                className={`${styles.actionButton} ${styles.printButton}`}
                                                                title={t('pages.stock.query.printLabel', 'Imprimir Placa')}
                                                            >
                                                                <FiPrinter size={18} />
                                                            </PDFDownloadLink>
                                                        );
                                                    })()}
                                                    {can.move && (
                                                        <button
                                                            className={`${styles.actionButton} ${styles.transferButton}`}
                                                            onClick={(e) => handleTransferClick(e, item)}
                                                            disabled={isLoading || isWater || item.totalQuantity === 0}
                                                            title={isWater ? t('pages.stock.query.waterNoTransfer', 'Água não pode ser transferida') : (item.totalQuantity === 0 ? t('pages.stock.query.noStockTransfer', 'Sem estoque para transferir') : `${t('pages.stock.query.transferTitle', 'Transferir')} ${item.rawMaterialName}`)}
                                                        >
                                                            <GoArrowSwitch size={18} />
                                                        </button>
                                                    )}

                                                    {expiryStatus && !isWater && can.verify && (
                                                        <button
                                                            className={`${styles.actionButton} ${styles.statusButton}`}
                                                            onClick={(e) => cycleExpiryStatus(e, item)}
                                                            disabled={updatingStatus}
                                                            title={expiryStatus === EXPIRY_STATUS.EXPIRED ?
                                                                t('pages.stock.query.markPendingReview', 'Marcar como "Aguardando Análise"') :
                                                                expiryStatus === EXPIRY_STATUS.PENDING_REVIEW ?
                                                                    t('pages.stock.query.markExpired', 'Marcar como "Vencido"') :
                                                                    t('pages.stock.query.resetExpired', 'Resetar para "Vencido"')}
                                                            style={{
                                                                backgroundColor: EXPIRY_STATUS_CONFIG[expiryStatus]?.bgColor || '#718096',
                                                                color: 'white',
                                                                border: 'none',
                                                                borderRadius: '8px',
                                                                width: '36px',
                                                                height: '36px',
                                                                display: 'inline-flex',
                                                                alignItems: 'center',
                                                                justifyContent: 'center',
                                                                cursor: 'pointer',
                                                                opacity: updatingStatus ? 0.5 : 1,
                                                                fontSize: '18px'
                                                            }}
                                                        >
                                                            {expiryStatus === EXPIRY_STATUS.EXPIRED ? <GiSandsOfTime size={18} /> :
                                                                expiryStatus === EXPIRY_STATUS.PENDING_REVIEW ? <PiKeyReturnLight size={18} /> : <IoReload size={18} />}
                                                        </button>
                                                    )}
                                                </div>
                                            </td>
                                        </tr>
                                    );
                                })
                            )}
                            {(!isLoading && currentStockPage.length === 0) && (
                                <tr>
                                    <td colSpan={8}>
                                        <EmptyState
                                            title={searchTerm || filterProductTypeId || filterCategoryId ?
                                                t('pages.stock.query.noItemsWithFilters', 'Nenhum item encontrado com os filtros aplicados') :
                                                t('pages.stock.query.noItems', 'Nenhum item encontrado no estoque.')}
                                            message={searchTerm || filterProductTypeId || filterCategoryId ?
                                                t('pages.stock.query.tryDifferentFilters', 'Tente ajustar os filtros de busca') :
                                                t('pages.stock.query.startAdding', 'Comece registrando entradas de estoque')}
                                        />
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>

                <Pagination
                    currentPage={page}
                    totalPages={totalPages}
                    totalItems={totalFilteredItems}
                    onPageChange={handlePageChange}
                    disabled={isLoading}
                    itemsLabel={t('pages.stock.query.itemPlural', 'itens')}
                />
            </div>
        </div>
    );
};

export default StockQueryPage;